import { useTranslation } from 'react-i18next';

import { EntityAvatar } from '@/modules/common/components/molecules/avatar/entity-avatar';
import { OpsiumUserShort } from '@/modules/common/types/opsium-user';
import { getMembershipTypeLabel } from '@/modules/team/utils/membership-type.utils';

import { useTeamMemberInfoBestEffort } from '../../hooks/use-team-member-info-best-effort';

interface TimeOffMemberInfoCardProps {
  /** The requester, always available from the request details. */
  requestedBy: OpsiumUserShort;
  /** Days the request books — rendered as a "Requested N days" chip inside the card. */
  requestedDays: number;
}

interface InfoRowProps {
  label: string;
  value: React.ReactNode;
}

const InfoRow = ({ label, value }: InfoRowProps) => (
  <div className="flex items-start justify-between gap-3 text-sm">
    <span className="shrink-0 text-muted-foreground">{label}</span>
    <span className="min-w-0 truncate text-right font-medium">{value}</span>
  </div>
);

/**
 * Approve-mode card with the requester's identity. Name + avatar come from the
 * request itself; the richer details (department, seniority, role, membership
 * type, manager, location) are fetched best-effort and each row is shown only
 * when its value is available — omitted silently otherwise.
 */
export const TimeOffMemberInfoCard = ({
  requestedBy,
  requestedDays,
}: TimeOffMemberInfoCardProps) => {
  const { t } = useTranslation(['authenticated.timeOff', 'authenticated.team']);
  const { data: member } = useTeamMemberInfoBestEffort(requestedBy.id);

  const rows: InfoRowProps[] = [];
  if (member?.department?.name) {
    rows.push({
      label: t('approvals.detail.memberCard.department'),
      value: member.department.name,
    });
  }
  if (member?.seniority?.name) {
    rows.push({ label: t('approvals.detail.memberCard.seniority'), value: member.seniority.name });
  }
  if (member?.role?.name) {
    rows.push({ label: t('approvals.detail.memberCard.role'), value: member.role.name });
  }
  if (member?.membershipType) {
    rows.push({
      label: t('approvals.detail.memberCard.membershipType'),
      value: getMembershipTypeLabel(member.membershipType),
    });
  }
  if (member?.manager?.name) {
    rows.push({
      label: t('approvals.detail.memberCard.manager'),
      // Small rounded avatar + name so the manager reads as a person, not a string.
      value: (
        <span className="flex min-w-0 items-center justify-end gap-2">
          <EntityAvatar
            image={member.manager.profileImageUrl}
            name={member.manager.name}
            size="xs"
            shape="rounded"
          />
          <span className="truncate">{member.manager.name}</span>
        </span>
      ),
    });
  }
  if (member?.location) {
    rows.push({ label: t('approvals.detail.memberCard.location'), value: member.location });
  }

  return (
    <div className="flex flex-col gap-4 rounded-xl border bg-surface p-5">
      <EntityAvatar
        image={requestedBy.profileImageUrl}
        name={requestedBy.name}
        size="lg"
        shape="rounded"
        withBorder
      />

      <span className="truncate text-lg font-bold underline underline-offset-4">
        {requestedBy.name}
      </span>

      {rows.length > 0 && (
        <div className="flex flex-col gap-2">
          {rows.map(row => (
            <InfoRow key={row.label} label={row.label} value={row.value} />
          ))}
        </div>
      )}
      <div className="rounded-lg bg-muted-foreground/15 py-2 text-center text-sm font-medium">
        {t('approvals.detail.summary.requestedDays', { count: requestedDays })}
      </div>
    </div>
  );
};
